import { creatorbaseQueries, musicQueries, spotifyQueries, SpotifyTimeSeriesResponse } from '@round/api';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { useCallback } from 'react';

const spotifyPopularityOptions = spotifyQueries.timeSeries.options.list;
const creatorbaseSongOptions = creatorbaseQueries.song.options.detail;
const musicSongOptions = musicQueries.song.options.detail;

export function useCurrentSpotifyPopularity(creatorbaseSongId: number | null | undefined) {
    const {
        data: songData,
        isLoading: isSongDataLoading,
        isError: isSongDataError,
    } = useQuery({
        ...creatorbaseSongOptions(creatorbaseSongId),
        enabled: !!creatorbaseSongId,
    });

    const {
        data: musicSongData,
        isLoading: isMusicSongDataLoading,
        isError: isMusicSongDataError,
    } = useQuery({
        ...musicSongOptions(songData?.music_song_id),
        enabled: !!songData?.music_song_id,
    });

    const spotifySongId = musicSongData?.spotify_id;

    const { data, isLoading, isError } = useQuery({
        ...spotifyPopularityOptions({
            type: 'song',
            fields: ['popularity'],
            ids: spotifySongId ? [spotifySongId] : [],
            start_date: moment().subtract(7, 'days').format('YYYY-MM-DD'),
            end_date: moment().format('YYYY-MM-DD'),
        }),
        select: useCallback(
            (data: SpotifyTimeSeriesResponse) => {
                if (!spotifySongId) {
                    return null;
                }

                const popularitySeries = data[spotifySongId]?.popularity;

                if (!popularitySeries) {
                    return null;
                }

                return popularitySeries[popularitySeries.length - 1].value;
            },
            [spotifySongId]
        ),
        enabled: !!spotifySongId,
    });

    const hasData = typeof data === 'number';
    // Only show the indicator once a popularity value has been successfully fetched -
    // never a loading or error state, since we can't be sure the data exists until it
    // arrives.
    const shouldExpectNoData = songData?.status === 'unreleased' || !hasData;

    return {
        data,
        isLoading: isLoading || isSongDataLoading || isMusicSongDataLoading,
        isError: isError || isSongDataError || isMusicSongDataError,
        shouldExpectNoData,
    };
}
